import React, { useState } from 'react';
import {
	Modal,
	Box,
	SpaceBetween,
	Button,
	FormField,
	Input,
	Checkbox,
	Alert,
} from '@amzn/awsui-components-console';

interface CopyWorkflowModalProps {
	visible: boolean;
	onDismiss: () => void;
	onConfirm: (newWorkflowName: string) => Promise<string | void>;
	isCopying: boolean;
	sourceWorkflowName: string;
	testIdPrefix?: string;
}

const CopyWorkflowModal: React.FC<CopyWorkflowModalProps> = ({
	visible,
	onDismiss,
	onConfirm,
	isCopying,
	sourceWorkflowName,
	testIdPrefix = 'copy-workflow',
}) => {
	const [newWorkflowName, setNewWorkflowName] = useState('');
	const [validationError, setValidationError] = useState('');
	const [apiError, setApiError] = useState('');
	const [acknowledged, setAcknowledged] = useState(false);

	const handleConfirm = async () => {
		if (!newWorkflowName.trim()) {
			setValidationError('Workflow name is required');

			return;
		}

		setApiError('');
		const error = await onConfirm(newWorkflowName.trim());

		if (error) {
			setApiError(error);
		}
	};

	const handleDismiss = () => {
		setNewWorkflowName('');
		setValidationError('');
		setApiError('');
		setAcknowledged(false);
		onDismiss();
	};

	const handleNameChange = (value: string) => {
		setNewWorkflowName(value);
		if (validationError) {
			setValidationError('');
		}

		if (apiError) {
			setApiError('');
		}
	};

	return (
		<Modal
			visible={visible}
			onDismiss={handleDismiss}
			header="Copy Workflow"
			closeAriaLabel="Close modal"
			size="medium"
			footer={
				<Box>
					<SpaceBetween direction="vertical" size="s">
						<Checkbox
							checked={acknowledged}
							onChange={({ detail }) => setAcknowledged(detail.checked)}
							data-testid={`${testIdPrefix}-acknowledge`}
						>
							I acknowledge this workflow is created for new functionality
						</Checkbox>
						<Box float="right">
							<SpaceBetween direction="horizontal" size="xs">
								<Button
									variant="link"
									onClick={handleDismiss}
									disabled={isCopying}
								>
									Cancel
								</Button>
								<Button
									variant="primary"
									onClick={handleConfirm}
									loading={isCopying}
									disabled={!acknowledged}
									data-testid={`${testIdPrefix}-confirm`}
								>
									Copy
								</Button>
							</SpaceBetween>
						</Box>
					</SpaceBetween>
				</Box>
			}
		>
			<SpaceBetween size="m">
				<Alert type="info" header="Tip">
					You do not need to copy a workflow to execute it for the same
					functionality. You can reuse the already existing workflow. Only copy
					if you need to create a new workflow with different configuration or
					functionality.
				</Alert>
				{apiError && (
					<Alert type="error" dismissible onDismiss={() => setApiError('')}>
						{apiError}
					</Alert>
				)}
				<Box variant="span">
					Create a copy of workflow{' '}
					<Box variant="span" fontWeight="bold">
						{sourceWorkflowName}
					</Box>
				</Box>
				<FormField
					label="New Workflow Name"
					description="Use descriptive names with underscores or hyphens (e.g., FC_LAUNCH_WORKFLOW_US_EAST)"
					errorText={validationError}
					data-testid={`${testIdPrefix}-name-field`}
				>
					<Input
						value={newWorkflowName}
						onChange={({ detail }) => handleNameChange(detail.value)}
						placeholder="Enter new workflow name"
						disabled={isCopying}
						data-testid={`${testIdPrefix}-name-input`}
					/>
				</FormField>
			</SpaceBetween>
		</Modal>
	);
};

export default CopyWorkflowModal;
